"use client";

/**
 * SponsorButton — pay-what-you-feel sponsorship / mentorship of any BB entity
 * or person. Per Gordon: "one should be able to sponsor or mentor financially
 * any content creator as well as the bb super admin user directly. also sponsor
 * mentor financially a track or a release, event, marketplace prototype,
 * artwork, music film production. pwyw"
 *
 * Routes through the existing /api/checkout `donation` type (π% platform fee,
 * Stripe Connect to the recipient's account) with optional entity context so
 * the contribution is recorded as a sponsorship of that specific track /
 * release / event / item / artwork / film. When no recipient Connect account
 * exists the charge still completes to the platform and is recorded for manual
 * settlement.
 *
 * kind:
 *   'sponsor' — back a *thing* (track, release, event, prototype, artwork, film)
 *   'mentor'  — back a *person* (creator, or the BB super-admin directly)
 */

import { useState } from "react";
import { HandHeart, HandCoins, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { ThemedPaymentDialog } from "@/components/payment/themed-payment-dialog";
import { cn } from "@/lib/utils";

const CURRENCY_SYMBOLS: Record<string, string> = { eur: "€", usd: "$", gbp: "£", cad: "$", sek: "kr" };
const FLOOR_CENTS = 100;

export type SponsorEntityType =
  | "creator" | "superadmin"
  | "track" | "release" | "event" | "marketplace" | "artwork" | "film"
  | "label" | "radio" | "podcast";

interface SponsorButtonProps {
  /** Recipient creator's user id (Connect destination). '' for platform/superadmin via env. */
  recipientId?: string;
  recipientName?: string;
  /** Entity this sponsorship is attributed to (optional — omit for pure person mentorship) */
  entityType: SponsorEntityType;
  entityId?: string;
  entityTitle?: string;
  /** Cover/thumbnail shown on the Stripe page + dialog */
  coverArtUrl?: string;
  currency?: string;
  /** 'sponsor' backs a thing, 'mentor' backs a person */
  kind?: "sponsor" | "mentor";
  /** Compact icon-only for cards */
  compact?: boolean;
  variant?: "outline" | "default" | "ghost" | "secondary";
  className?: string;
  label?: string;
}

const PRESETS = [200, 500, 1000, 2500, 5000, 10000];

export function SponsorButton({
  recipientId,
  recipientName,
  entityType,
  entityId,
  entityTitle,
  coverArtUrl,
  currency = "eur",
  kind = "sponsor",
  compact = false,
  variant = "outline",
  className,
  label,
}: SponsorButtonProps) {
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [cents, setCents] = useState(500);
  const [raw, setRaw] = useState("5.00");
  const [name, setName] = useState("");
  const [anon, setAnon] = useState(false);
  const [message, setMessage] = useState("");
  // Tier-2 embedded themed checkout (Payment Element on our own page).
  const [themed, setThemed] = useState<{ amount: number; body: any } | null>(null);

  const cur = currency.toLowerCase();
  const sym = CURRENCY_SYMBOLS[cur] || "€";
  const isMentor = kind === "mentor";
  // HandCoins, NOT a heart — Save (favorite) and Angel (funding) are two
  // different things, and both wearing hearts read as the same action
  // (Gordon: "Fan is funding money to the artist directly without buying
  // anything"). Mentor keeps the HandHeart.
  const Icon = isMentor ? HandHeart : HandCoins;
  // "Angel" (Gordon 2026-10-31: "change all the Fan button text to Angel" —
  // backers are BB's Angels, see /supporters). Internal kind stays "sponsor".
  const verb = isMentor ? "Support" : "Angel";
  const buttonLabel = label || verb;

  const openDialog = () => {
    if (userProfile && !name) {
      setName((userProfile as any).nickname || userProfile.displayName || "");
    }
    setOpen(true);
  };

  // Hand off to the embedded themed Payment Element instead of redirecting.
  const pay = () => {
    const amount = Math.max(cents, FLOOR_CENTS);
    setOpen(false);
    setThemed({
      amount,
      body: {
        type: "donation",
        amountCents: amount,
        currency: cur,
        recipientId: recipientId || "",
        recipientName: recipientName || entityTitle || "the creator",
        buyerName: anon ? "" : name.trim(),
        isAnonymous: anon,
        customerEmail: user?.email || undefined,
        sponsorKind: kind,
        sponsorEntityType: entityType,
        sponsorEntityId: entityId || "",
        sponsorEntityTitle: entityTitle || recipientName || "",
        sponsorMessage: message.trim().slice(0, 500),
      },
    });
  };

  return (
    <>
      <Button
        size={compact ? "icon" : "sm"}
        variant={variant}
        className={cn(compact ? "h-8 w-8 rounded-full" : "gap-1.5", className)}
        title={compact ? `${verb} ${entityTitle || recipientName || ""}` : undefined}
        onClick={openDialog}
      >
        <Icon className={cn("h-4 w-4", !compact && "mr-0.5")} />
        {!compact && buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[400px] max-sm:fixed max-sm:inset-x-0 max-sm:top-[50px] max-sm:bottom-0 max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!left-0 max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:overflow-y-auto max-sm:h-auto max-sm:pb-[calc(84px+env(safe-area-inset-bottom))]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="h-5 w-5 text-primary" />
              {verb} · Pay What You Feel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Target */}
            <div className="flex gap-3 items-center">
              {coverArtUrl && (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={coverArtUrl} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className="font-medium truncate">{entityTitle || recipientName || "Breakin' Bread"}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {isMentor ? "Support" : `Angel · ${entityType}`}
                  {recipientName && entityTitle ? ` · ${recipientName}` : ""}
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-2.5">
              <p className="text-sm font-medium flex items-center gap-1.5">
                <Sparkles className="h-4 w-4 text-primary" />
                {isMentor
                  ? <>Support this maker — your support goes straight to {recipientName || "the creator"}.</>
                  : <>Fund {recipientName || "the artist"} directly — this is backing, not buying.</>}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {isMentor
                  ? <>Pay according to how much you believe in it. </>
                  : <>You get nothing shipped and nothing unlocked — your money funds the artist and their work, like helping a demo become a finished official release. </>}
                Minimum {sym}{(FLOOR_CENTS / 100).toFixed(2)}. Breakin&apos; Bread keeps a small share to keep the bakery running.
              </p>
            </div>

            {/* Presets */}
            <div className="flex flex-wrap justify-center gap-2">
              {PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => { setCents(c); setRaw((c / 100).toFixed(2)); }}
                  className={cn(
                    "rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors",
                    cents === c ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"
                  )}
                >
                  {sym}{(c / 100).toFixed(0)}
                </button>
              ))}
            </div>

            {/* Custom amount */}
            <div className="flex items-center justify-center gap-2">
              <span className="text-3xl font-bold">{sym}</span>
              <Input
                type="number"
                step="0.01"
                min={FLOOR_CENTS / 100}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                onBlur={(e) => {
                  const c = Math.max(Math.round((parseFloat(e.target.value) || 0) * 100), FLOOR_CENTS);
                  setCents(c); setRaw((c / 100).toFixed(2));
                }}
                className="h-14 w-full max-w-[14rem] text-center !text-2xl font-bold"
              />
            </div>

            {/* Optional message — words of encouragement or constructive
                feedback that go to the maker with the contribution. Per Gordon:
                "possibility to add a few words as a mentor/sponsor." */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                {isMentor ? "A few words of support" : "A note with your support"} <span className="font-normal">(optional)</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder={isMentor ? "Encouragement, advice, a little wisdom…" : "Why you're backing this, a word of encouragement…"}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            {/* Name / anon */}
            <div className="space-y-2">
              <Input
                value={anon ? "" : name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                disabled={anon}
                className="h-9"
              />
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} className="accent-primary" />
                Stay anonymous
              </label>
            </div>

            <Button className="w-full" onClick={pay}>
              {`${verb} ${sym}${(cents / 100).toFixed(2)}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tier-2 embedded themed checkout */}
      {themed && (
        <ThemedPaymentDialog
          open={!!themed}
          onOpenChange={(o) => { if (!o) setThemed(null); }}
          body={themed.body}
          amountCents={themed.amount}
          currency={cur}
          title={`${verb} ${entityTitle || recipientName || "Breakin' Bread"}`}
          subtitle={isMentor ? "Support" : `Angel · ${entityType}`}
          coverArtUrl={coverArtUrl}
          payLabel={verb}
          onPaid={() => {
            setThemed(null);
            toast({
              title: isMentor ? "Thank you for mentoring 🌾" : "Thank you for being an Angel 🌾",
              description: `Your support goes straight to ${recipientName || "the creator"}.`,
            });
          }}
        />
      )}
    </>
  );
}
